import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useOfferList, useFilterSoldOutOffers } from 'hooks/Offers/useOfferList'
import OfferListLoadMoreButton from './OfferListLoadMoreButton'
import styled from 'styled-components'
import { rem } from 'polished'
import { fillArray, sortBy, take } from 'lib/arrayUtils'
import OfferListOfferTile from './OfferListOfferTile'
import noop from 'lib/noop'
import useMountedEffect from 'hooks/useMountedEffect'
import Typography from 'components/Luxkit/Typography/Typography'
import OfferListLoadingOfferTile from './OfferListLoadingOfferTile'
import OfferListEventsContext, { OfferListEvents } from './OfferListEventsContext'

import { viewItemListOffers } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import { getSearchTypeFromFilters } from '../../../lib/searchUtils'
import { useAppSelector } from 'hooks/reduxHooks'

const OfferGrid = styled.div`
  display: grid;
  justify-items: center;
  grid-gap: ${rem(16)};
  width: 100%;
  grid-template-columns: 1fr;
`

const ListItem = styled.div`
  width: 100%;

  /* https://css-tricks.com/preventing-a-grid-blowout/ */
  min-width: 0;
`
//
function tryGetOfferListLoaderType(filters: App.OfferListFilters) {
  if (filters.offerTypes?.length === 1) {
    return filters.offerTypes[0]
  }
}

export interface OfferListV2Item {
  id: string;
  position: number | 'start' | 'end';
  show?: boolean | 'hasOffers' | 'hasNoOffers' | 'always';
  columnWidth?: number;
  element?: React.ReactNode;
}

interface Props {
  className?: string;
  filters?: App.OfferListFilters;
  interstitials?: Array<OfferListV2Item>;
  random?: boolean;
  pageSize?: number;
  eagerLoadFirstImage?: boolean;
  tileStyle?: 'frontpage' | 'search';
  defaultPage?: number;
  onPageChange?: (nextPage: number) => void;
  maxOffers?: number | 'infinite';
  // Adds the ability to provide your own offer list
  offerlistOverride?: App.OfferList;
}

/**
 * Features to add support for:
 * - excluded offer ids
 * - minimum offers before displaying
 */
function OfferList(props: Props) {
  const {
    className,
    filters,
    interstitials,
    random,
    pageSize,
    eagerLoadFirstImage,
    tileStyle,
    defaultPage,
    onPageChange,
    maxOffers,
    offerlistOverride,
  } = props
  const dataLoaded = useRef(false)
  const [page, setPage] = useState<number>(defaultPage ?? 0)
  const fireOfferListEvent = useContext(OfferListEventsContext) ?? noop
  let offerIds: string [] = []

  const incrementPage = useCallback(() => {
    setPage((prevState) => prevState + 1)
  }, [])

  useMountedEffect(() => {
    onPageChange(page)
    // eslint-disable-next-line
  }, [page])

  const [localOfferlist, listKey] = useOfferList(filters, { randomize: random, disabled: !!offerlistOverride })

  const searchType = getSearchTypeFromFilters(filters)

  const selectedOfferList = offerlistOverride ?? localOfferlist
  const filteredOfferList = useFilterSoldOutOffers(selectedOfferList)

  // For searches on destinations, we filter any offers that are sold out
  const offerList = searchType === 'destination' ? filteredOfferList : selectedOfferList

  const fetching = offerList.fetching
  // quick dirty optimisation to not pass filters to front page and break memoisation
  // only search style tiles *require* the filters object
  const searchFilters = tileStyle === 'search' ? filters : undefined
  const shownElementCount = page * pageSize

  const totalOfferCount = Math.min(
    (maxOffers === 'infinite' ? offerList.offerIds.length : maxOffers),
    offerList.offerIds.length,
  )

  const stateOffers = useAppSelector<App.OfferState>((state) => state.offer)

  useEffect(() => {
    if (!offerList.fetching) {
      fireOfferListEvent(
        OfferListEvents.listLoad,
        { key: listKey, list: offerList },
      )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerList])

  useEffect(() => {
    if (offerIds && offerIds.length && !dataLoaded.current) {
      const event = viewItemListOffers(stateOffers, offerList.offerIds)
      if (event) {
        addGTMEvent(event)
        dataLoaded.current = true
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerIds])

  let offerItems: Array<OfferListV2Item>

  if (fetching) {
    // list is fetching, return 4 placeholder tiles of the current style
    offerItems = fillArray(4).map<OfferListV2Item>((index) => ({
      id: `${index}-loader`,
      position: 'start',
      element: <OfferListLoadingOfferTile
        tileStyle={tileStyle}
        offerType={tryGetOfferListLoaderType(filters)}
      />,
    }))
  } else {
    offerIds = take(offerList.offerIds, maxOffers === 'infinite' ? shownElementCount : maxOffers)
    // maximum number of offers we'll show is the max count, so only process them
    offerItems = offerIds.map<OfferListV2Item>((id, index) => ({
      id,
      position: index,
      element: <OfferListOfferTile
        offerId={id}
        position={index}
        eagerLoadImage={index === 0 && eagerLoadFirstImage}
        filters={searchFilters}
        tileStyle={tileStyle}
      />,
    }))
  }

  // Extras are added first as if they have a position set, we want to that to go first
  const filteredExtras = interstitials.filter(item => {
    // we don't know what the 'end' or a position is until we actually have the list
    if (item.position !== 'start' && fetching) return false

    if (item.show === 'always') return true
    if (item.show === 'hasOffers') return !fetching && offerItems.length > 0
    if (item.show === 'hasNoOffers') return !fetching && offerItems.length === 0
    // wondering why your item is showing when it shouldn't? Always cast to a boolean
    if (typeof item.show === 'boolean') return item.show

    return true
  })

  const combinedItems = filteredExtras.concat(offerItems)
  const visibleItems = sortBy(combinedItems, item => {
    if (item.position === 'end') {
      return 10000
    } else if (item.position === 'start') {
      return -1
    } else {
      return item.position
    }
  }, 'asc')

  const totalItemCount = totalOfferCount + filteredExtras.length
  const hasMore = totalItemCount > shownElementCount

  return (
    <OfferGrid
      className={className}
      data-testid="offer-list"
    >
      {take(visibleItems, shownElementCount).map(item => <ListItem key={item.id}>
        {item.element}
      </ListItem>,
      )}
      {hasMore && <ListItem>
        <Typography align="center">
          <OfferListLoadMoreButton onClick={incrementPage} size="large" onScrollNear={incrementPage}>
            Load more
          </OfferListLoadMoreButton>
        </Typography>
      </ListItem>}
    </OfferGrid>
  )
}

OfferList.defaultProps = {
  filters: {},
  interstitials: [],
  tileStyle: 'frontpage',
  onPageChange: noop,
  defaultPage: 1,
  pageSize: 4,
  maxOffers: 'infinite',
}

export default OfferList
